import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ExternalLink, Map, BookOpen, ClipboardCheck, Award, FolderLock, FileUp, Flame, AlertTriangle } from "lucide-react";

const SITE = "https://anydegreenow.com";

const STEPS = [
  { icon: Map, title: "Curriculum generation", body: "Describe the field you want to study (e.g. \"Full-Stack Software Engineering\") and the AI builds a full 120-credit degree map of 3-credit courses across 100-, 200-, 300- and 400-level progression." },
  { icon: BookOpen, title: "Course content", body: "Each course is generated on demand with ten in-depth units of 6,000+ words each, written section by section for textbook-quality depth." },
  { icon: ClipboardCheck, title: "Assessments", body: "Every course includes an AI-generated quiz, midterm and final exam, all auto-graded with explanations." },
  { icon: Award, title: "Capstone & graduation", body: "Once all courses are complete, you write a capstone thesis that the AI evaluates against academic rubrics, issuing a final grade and a digital degree certificate." },
  { icon: FolderLock, title: "Credentials vault", body: "View and download digital diplomas, official transcripts and per-course completion certificates." },
  { icon: FileUp, title: "Eligibility & transfer credits", body: "Upload prior transcripts and diplomas for AI verification and transfer-credit evaluation toward your degree." },
];

function Step({ icon: Icon, title, body, i }) {
  return (
    <div className="card-tile transition-transform duration-300 hover:-translate-y-1" data-testid={`uni-step-${i}`}>
      <Icon className="text-cyan-400" size={22} />
      <h3 className="font-display text-lg font-bold mt-3">{title}</h3>
      <p className="text-sm text-slate-400 mt-2">{body}</p>
    </div>
  );
}

function Payments() {
  return (
    <div className="card-tile mt-10 border-orange-500/40" data-testid="uni-payments">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Flame className="text-orange-400" /> Pay with FLAME</h2>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        <div><div className="text-xs text-slate-500">Price per course</div><div className="font-mono text-2xl font-bold text-orange-300" data-testid="uni-price">1,000 FLAME</div></div>
        <div><div className="text-xs text-slate-500">To the Treasury Safe</div><div className="font-mono text-2xl font-bold" data-testid="uni-split-treasury">90%</div></div>
        <div><div className="text-xs text-slate-500">To a partner wallet</div><div className="font-mono text-2xl font-bold" data-testid="uni-split-partner">10%</div></div>
      </div>
      <p className="text-sm text-slate-400 mt-4">Course access is paid in FLAME, a token on the Cronos blockchain, split across two on-chain transfers. Earn FLAME by burning CRO and playing here, then claim it to your wallet, or trade for it on VVS Finance.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/dashboard" className="btn-tactile btn-ghost text-sm" data-testid="uni-earn-link">Earn FLAME</Link>
        <Link to="/buy" className="btn-tactile btn-ghost text-sm" data-testid="uni-buy-link">Buy FLAME</Link>
      </div>
    </div>
  );
}

export default function UniversityPage() {
  return (
    <div className="pt-24 pb-20" data-testid="university-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="chip inline-flex items-center gap-1 text-cyan-300 border-cyan-500/40"><GraduationCap size={12} /> Spend your FLAME</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-4">AI University</h1>
        <p className="text-base md:text-lg text-slate-300 mt-4 max-w-3xl">An autonomous online learning platform that builds a custom, AI-driven Bachelor's degree around your goals. There's no fixed catalog: describe what you want to study and the AI creates a complete 120-credit curriculum on demand, with course outlines, textbook-length units, assessments and a capstone thesis.</p>
        <a href={SITE} target="_blank" rel="noreferrer" className="btn-tactile btn-flame mt-6 inline-flex items-center gap-2" data-testid="uni-visit-button">Visit anydegreenow.com <ExternalLink size={14} /></a>

        <h2 className="font-display text-2xl font-bold mt-14">How it works</h2>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEPS.map((s, i) => <Step key={s.title} {...s} i={i} />)}
        </div>

        <Payments />

        <div className="card-tile mt-10 border-amber-500/40" data-testid="uni-accreditation">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><AlertTriangle size={18} className="text-amber-400" /> About accreditation</h2>
          <p className="text-sm text-slate-300 mt-2">AI University delivers education at a fraction of traditional tuition, powered by AI rather than overhead. However, its degrees are <b>not accredited by the U.S. Department of Education</b>, and other colleges and universities may or may not accept credits earned there. Check transfer policies with any institution you plan to attend.</p>
        </div>
        <p className="text-xs text-slate-500 mt-6">AI University is a separate website at anydegreenow.com. Course content, grading and credentials are provided there, not by this game.</p>
      </div>
    </div>
  );
}
